
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Plus, Search, Trash2, Edit2, X, Check, Upload, Download, Folder, FolderPlus, ArrowRightLeft, HelpCircle, ChevronDown, ChevronRight, Globe } from 'lucide-react';
import * as XLSX from 'xlsx';
import { SynonymGroup } from '../types';
import { LANGUAGES } from '../utils/languages';

import SynonymGroupEditor from './SynonymGroupEditor';
import SynonymImportModal from './SynonymImportModal';

// Helper: tra cứu flag + label native từ code (vd 'vi' → '🇻🇳 Tiếng Việt')
const getLangInfo = (code?: string) => {
    if (!code) return null;
    const lc = code.toLowerCase().trim();
    return LANGUAGES.find(l => l.code.toLowerCase() === lc)
        || LANGUAGES.find(l => l.code.toLowerCase().slice(0, 2) === lc.slice(0, 2))
        || null;
};

// Badge cho cặp ngôn ngữ — render `🇻🇳 VI → EN 🇬🇧`
const LangPairBadge: React.FC<{ source?: string; target?: string }> = ({ source, target }) => {
    const s = getLangInfo(source);
    const t = getLangInfo(target);
    if (!source && !target) {
        return <span className="text-xs text-slate-400 italic">—</span>;
    }
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-violet-50 text-violet-700 rounded text-[11px] border border-violet-100 whitespace-nowrap font-medium">
            <span>{s?.flag || '🌐'}</span>
            <span className="font-bold uppercase">{(source || '?').slice(0, 2)}</span>
            <ArrowRightLeft className="w-2.5 h-2.5 opacity-60" />
            <span className="font-bold uppercase">{(target || '?').slice(0, 2)}</span>
            <span>{t?.flag || '🌐'}</span>
        </span>
    );
};




interface TerminologyTabProps {
    // Synonym Groups (single source of truth)
    synonymGroups?: SynonymGroup[];
    onUpdateSynonymGroups?: (groups: SynonymGroup[]) => void;
    // Phase 2+3: Global Glossary integration
    globalSynonymGroups?: SynonymGroup[];
    projectId?: string;
    projectName?: string;
    onAddToGlobal?: (groups: SynonymGroup[]) => void;
    // Trigger TermSelector modal (Nhập từ Từ điển chung) — owned by parent
    onPickFromGlobal?: () => void;
    // Default lang pair — inherit khi tạo group mới (vd 'vi', 'en')
    defaultSourceLang?: string;
    defaultTargetLang?: string;
}

// Default colors for groups
const GROUP_COLORS = [
    '#6366f1', // indigo
    '#8b5cf6', // violet
    '#ec4899', // pink
    '#f59e0b', // amber
    '#10b981', // emerald
    '#06b6d4', // cyan
    '#3b82f6', // blue
    '#f97316', // orange
];

// Inline Category Dropdown Component
const CategoryDropdown: React.FC<{
    currentCategory?: string;
    allCategories: string[];
    onSelect: (category: string) => void;
}> = ({ currentCategory, allCategories, onSelect }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [newCatName, setNewCatName] = useState('');
    const [showCreateInput, setShowCreateInput] = useState(false);

    const handleSelect = (cat: string) => {
        onSelect(cat);
        setIsOpen(false);
    };

    const handleCreateNew = () => {
        if (newCatName.trim()) {
            onSelect(newCatName.trim());
            setNewCatName('');
            setShowCreateInput(false);
            setIsOpen(false);
        }
    };

    return (
        <div className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`px-2.5 py-1 rounded text-xs border flex items-center gap-1 transition-colors whitespace-nowrap max-w-full ${currentCategory
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                    : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200'
                    }`}
            >
                <Folder className="w-3 h-3 shrink-0" />
                <span className="truncate">{currentCategory || 'Chọn thư mục'}</span>
                <ChevronDown className="w-3 h-3 shrink-0" />
            </button>

            {isOpen && (
                <div className="absolute z-50 top-full mt-1 left-0 w-48 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
                    {/* Existing categories */}
                    <div className="max-h-40 overflow-y-auto">
                        {allCategories.length === 0 ? (
                            <div className="px-3 py-2 text-xs text-slate-400 italic">Chưa có danh mục nào</div>
                        ) : (
                            allCategories.map(cat => (
                                <button
                                    key={cat}
                                    onClick={() => handleSelect(cat)}
                                    className={`w-full px-3 py-2 text-left text-xs hover:bg-indigo-50 flex items-center gap-2 ${currentCategory === cat ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-slate-700'
                                        }`}
                                >
                                    <Folder className="w-3 h-3" />
                                    {cat}
                                    {currentCategory === cat && <Check className="w-3 h-3 ml-auto" />}
                                </button>
                            ))
                        )}
                    </div>

                    {/* Clear category */}
                    {currentCategory && (
                        <button
                            onClick={() => handleSelect('')}
                            className="w-full px-3 py-2 text-left text-xs text-red-600 hover:bg-red-50 border-t border-slate-100"
                        >
                            <X className="w-3 h-3 inline mr-1" /> Xóa danh mục
                        </button>
                    )}

                    {/* Create new */}
                    <div className="border-t border-slate-100">
                        {showCreateInput ? (
                            <div className="p-2 flex gap-1">
                                <input
                                    type="text"
                                    value={newCatName}
                                    onChange={(e) => setNewCatName(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleCreateNew()}
                                    placeholder="Danh mục mới..."
                                    className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded focus:border-indigo-400 outline-none"
                                    autoFocus
                                />
                                <button
                                    onClick={handleCreateNew}
                                    className="p-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                                >
                                    <Check className="w-3 h-3" />
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => setShowCreateInput(true)}
                                className="w-full px-3 py-2 text-left text-xs text-indigo-600 hover:bg-indigo-50 flex items-center gap-2"
                            >
                                <FolderPlus className="w-3 h-3" /> Tạo danh mục mới
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Click outside to close */}
            {isOpen && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => { setIsOpen(false); setShowCreateInput(false); }}
                />
            )}
        </div>
    );
};

const TerminologyTab: React.FC<TerminologyTabProps> = ({
    synonymGroups = [],
    onUpdateSynonymGroups,
    globalSynonymGroups = [],
    projectId,
    projectName,
    onAddToGlobal,
    onPickFromGlobal,
    defaultSourceLang,
    defaultTargetLang,
}) => {
    // State for Synonym Groups
    const [groups, setGroups] = useState<SynonymGroup[]>(synonymGroups);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingGroup, setEditingGroup] = useState<SynonymGroup | undefined>(undefined);
    const [showGroupEditor, setShowGroupEditor] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    // Action menus (Add / Export)
    const [addMenuOpen, setAddMenuOpen] = useState(false);
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const addMenuRef = React.useRef<HTMLDivElement>(null);
    const exportMenuRef = React.useRef<HTMLDivElement>(null);
    const bulkCatRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        const handler = (e: MouseEvent) => {
            // Skip outside-close khi tour driver overlay đang active (giữ menu mở để show user)
            if (document.querySelector('.driver-active-element')) return;
            if (addMenuRef.current && !addMenuRef.current.contains(e.target as Node)) setAddMenuOpen(false);
            if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) setExportMenuOpen(false);
            if (bulkCatRef.current && !bulkCatRef.current.contains(e.target as Node)) setBulkCatOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);
    // Tour controls — Stage4 dispatch events để mở/đóng dropdown khi tới step term-add/term-export
    React.useEffect(() => {
        const openAdd = () => { setAddMenuOpen(true); setExportMenuOpen(false); };
        const openExport = () => { setExportMenuOpen(true); setAddMenuOpen(false); };
        const closeAll = () => { setAddMenuOpen(false); setExportMenuOpen(false); };
        window.addEventListener('tour-open-add-menu', openAdd);
        window.addEventListener('tour-open-export-menu', openExport);
        window.addEventListener('tour-close-menus', closeAll);
        return () => {
            window.removeEventListener('tour-open-add-menu', openAdd);
            window.removeEventListener('tour-open-export-menu', openExport);
            window.removeEventListener('tour-close-menus', closeAll);
        };
    }, []);
    // Export sang XLSX (round-trip: 1 row per target variant để giữ context + isDefault + lang)
    const handleExportCsv = useCallback(() => {
        const rows: Record<string, string>[] = [];
        for (const g of groups) {
            const sourceStr = g.sources.join(' | ');
            const sLang = g.sourceLang || defaultSourceLang || '';
            const tLang = g.targetLang || defaultTargetLang || '';
            for (const t of g.targets) {
                rows.push({
                    'Từ gốc': sourceStr,
                    'Bản dịch': t.text,
                    'Ngữ cảnh': t.context || '',
                    'Mặc định': t.isDefault ? 'yes' : '',
                    'Danh mục': g.category || '',
                    'Ngôn ngữ nguồn': sLang,
                    'Ngôn ngữ đích': tLang,
                });
            }
        }
        const headers = ['Từ gốc', 'Bản dịch', 'Ngữ cảnh', 'Mặc định', 'Danh mục', 'Ngôn ngữ nguồn', 'Ngôn ngữ đích'];
        const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
        ws['!cols'] = [{ wch: 30 }, { wch: 40 }, { wch: 18 }, { wch: 10 }, { wch: 18 }, { wch: 14 }, { wch: 14 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Thuật ngữ');
        const fileName = `terminology-${projectName || projectId || 'export'}-${new Date().toISOString().slice(0, 10)}.xlsx`;
        XLSX.writeFile(wb, fileName);
    }, [groups, projectName, projectId, defaultSourceLang, defaultTargetLang]);
    const [bulkCatInput, setBulkCatInput] = useState('');
    const [bulkCatOpen, setBulkCatOpen] = useState(false);

    // Sync internal state when synonymGroups prop changes (e.g., after async load)
    useEffect(() => {
        setGroups(synonymGroups);
    }, [synonymGroups]);

    // Check if a group exists in Global Glossary
    const isInGlobal = useCallback((group: SynonymGroup) => {
        return globalSynonymGroups.some(g =>
            g.sources.some(s => group.sources.some(gs => gs.toLowerCase() === s.toLowerCase())) &&
            g.targets.some(t => group.targets.some(gt => gt.text.toLowerCase() === t.text.toLowerCase()))
        );
    }, [globalSynonymGroups]);

    // Handle Add to Global
    const handleAddToGlobal = useCallback(() => {
        if (!onAddToGlobal) return;
        const localOnly = groups.filter(g => selectedIds.has(g.id) && !isInGlobal(g));
        if (localOnly.length === 0) return;

        const toAdd: SynonymGroup[] = localOnly.map(g => ({
            ...g,
            id: crypto.randomUUID(),
            sourceProjectId: projectId,
            sourceProjectName: projectName,
        }));
        onAddToGlobal(toAdd);
        setSelectedIds(new Set());
    }, [groups, selectedIds, isInGlobal, onAddToGlobal, projectId, projectName]);

    // Handlers for Synonym Groups
    const handleSaveGroup = (group: SynonymGroup) => {
        // Auto-inherit lang pair từ project nếu group chưa set
        const enriched: SynonymGroup = {
            ...group,
            sourceLang: group.sourceLang || defaultSourceLang,
            targetLang: group.targetLang || defaultTargetLang,
        };
        let newGroups = [...groups];
        const existingIdx = newGroups.findIndex(g => g.id === enriched.id);
        if (existingIdx >= 0) {
            newGroups[existingIdx] = enriched;
        } else {
            newGroups.unshift(enriched);
        }
        setGroups(newGroups);
        setShowGroupEditor(false);
        onUpdateSynonymGroups?.(newGroups);
    };

    const handleDeleteGroup = (groupId: string) => {
        if (!confirm('Bạn chắc muốn xoá thuật ngữ này?')) return;
        const newGroups = groups.filter(g => g.id !== groupId);
        setGroups(newGroups);
        onUpdateSynonymGroups?.(newGroups);
    };

    const handleImportGroups = (importedGroups: SynonymGroup[]) => {
        let newGroups = [...groups];
        importedGroups.forEach(imported => {
            const existingIdx = newGroups.findIndex(g => g.name.toLowerCase() === imported.name.toLowerCase());
            if (existingIdx >= 0) {
                // Merge sources
                imported.sources.forEach(s => {
                    if (!newGroups[existingIdx].sources.includes(s)) {
                        newGroups[existingIdx].sources.push(s);
                    }
                });
                // Merge targets
                imported.targets.forEach(t => {
                    const exists = newGroups[existingIdx].targets.some(existingT => existingT.text === t.text);
                    if (!exists) {
                        newGroups[existingIdx].targets.push(t);
                    }
                });
            } else {
                newGroups.push(imported);
            }
        });
        setGroups(newGroups);
        onUpdateSynonymGroups?.(newGroups);
    };

    // Handler to quickly update category of a single group
    const handleUpdateCategory = (groupId: string, newCategory: string) => {
        const newGroups = groups.map(g =>
            g.id === groupId ? { ...g, category: newCategory || undefined } : g
        );
        setGroups(newGroups);
        onUpdateSynonymGroups?.(newGroups);
    };

    // Bulk assign category to all selected keywords
    const handleBulkAssignCategory = (newCategory: string) => {
        const newGroups = groups.map(g =>
            selectedIds.has(g.id) ? { ...g, category: newCategory || undefined } : g
        );
        setGroups(newGroups);
        onUpdateSynonymGroups?.(newGroups);
        setSelectedIds(new Set()); // Clear selection after bulk action
    };

    // Toggle single selection
    const toggleSelect = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // Toggle all visible items
    const toggleSelectAll = () => {
        if (selectedIds.size === filteredGroups.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filteredGroups.map(g => g.id)));
        }
    };

    // Get all unique categories for dropdown
    const allCategories = useMemo(() => {
        const cats = new Set<string>();
        groups.forEach(g => {
            if (g.category && g.category.trim()) {
                cats.add(g.category);
            }
        });
        return Array.from(cats).sort();
    }, [groups]);

    // Filter and Group Logic
    const groupedItems = useMemo(() => {
        let filtered = groups;
        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            filtered = groups.filter(g =>
                g.name.toLowerCase().includes(lowerSearch) ||
                g.sources.some(s => s.toLowerCase().includes(lowerSearch)) ||
                g.targets.some(t => t.text.toLowerCase().includes(lowerSearch))
            );
        }

        const grouped: { [key: string]: SynonymGroup[] } = {};
        const noCategory: SynonymGroup[] = [];

        filtered.forEach(g => {
            if (g.category && g.category.trim()) {
                if (!grouped[g.category]) grouped[g.category] = [];
                grouped[g.category].push(g);
            } else {
                noCategory.push(g);
            }
        });

        // Sort keys
        const sortedKeys = Object.keys(grouped).sort();

        return { grouped, sortedKeys, noCategory };
    }, [groups, searchTerm]);

    // Active Categories state (for collapse/expand)
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

    // Expand all by default on first load or when search is active
    useEffect(() => {
        if (Object.keys(groupedItems.grouped).length > 0) {
            setExpandedCategories(new Set(Object.keys(groupedItems.grouped)));
        }
    }, [groups.length]); // Only reset when groups change significantly (import/load)

    // State for selected category filter
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

    // Filtered groups based on search and category
    const filteredGroups = useMemo(() => {
        let result = groups;

        // Filter by category
        if (selectedCategory === '__uncategorized__') {
            result = result.filter(g => !g.category || !g.category.trim());
        } else if (selectedCategory) {
            result = result.filter(g => g.category === selectedCategory);
        }

        // Filter by search
        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            result = result.filter(g =>
                g.name.toLowerCase().includes(lowerSearch) ||
                g.sources.some(s => s.toLowerCase().includes(lowerSearch)) ||
                g.targets.some(t => t.text.toLowerCase().includes(lowerSearch))
            );
        }

        return result;
    }, [groups, searchTerm, selectedCategory]);

    return (
        <div className="h-full flex gap-4">
            {/* Left Sidebar - Categories */}
            <div className="w-56 flex-shrink-0 flex flex-col gap-4">
                {/* Categories / Folders */}
                <div className="flex-1 flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                    <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                        <h3 className="text-xs font-black text-slate-500 uppercase tracking-wider">Danh mục</h3>
                        <span className="text-[10px] text-slate-400 font-medium">{groupedItems.sortedKeys.length}</span>
                    </div>

                    <div className="flex-1 overflow-y-auto p-2 space-y-1">
                        {/* All Groups */}
                        <button
                            onClick={() => setSelectedCategory(null)}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm font-medium transition-colors ${selectedCategory === null
                                ? 'bg-indigo-100 text-indigo-700'
                                : 'text-slate-600 hover:bg-slate-100'
                                }`}
                        >
                            <Folder className="w-4 h-4" />
                            <span className="flex-1">Tất cả</span>
                            <span className="text-xs text-slate-400">{groups.length}</span>
                        </button>

                        {/* Category List */}
                        {groupedItems.sortedKeys.map(cat => (
                            <button
                                key={cat}
                                onClick={() => setSelectedCategory(cat)}
                                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm font-medium transition-colors ${selectedCategory === cat
                                    ? 'bg-indigo-100 text-indigo-700'
                                    : 'text-slate-600 hover:bg-slate-100'
                                    }`}
                            >
                                <Folder className="w-4 h-4 text-amber-500" />
                                <span className="flex-1 truncate">{cat}</span>
                                <span className="text-xs text-slate-400">{groupedItems.grouped[cat].length}</span>
                            </button>
                        ))}

                        {/* Uncategorized */}
                        {groupedItems.noCategory.length > 0 && (
                            <button
                                onClick={() => setSelectedCategory('__uncategorized__')}
                                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm font-medium transition-colors ${selectedCategory === '__uncategorized__'
                                    ? 'bg-indigo-100 text-indigo-700'
                                    : 'text-slate-500 hover:bg-slate-100'
                                    }`}
                            >
                                <FolderPlus className="w-4 h-4 text-slate-400" />
                                <span className="flex-1 italic">Chưa phân loại</span>
                                <span className="text-xs text-slate-400">{groupedItems.noCategory.length}</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Content - Table View */}
            <div className="flex-1 flex flex-col min-w-0 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600">
                            <Folder className="w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-slate-800">Từ điển thuật ngữ</h2>
                            <p className="text-xs text-slate-500">
                                {selectedCategory === '__uncategorized__' ? 'Chưa phân loại' : selectedCategory || 'Tất cả'} - {filteredGroups.length} thuật ngữ
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="relative" data-tour="term-search">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Tìm kiếm thuật ngữ..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:border-indigo-500 outline-none w-64"
                            />
                        </div>
                        {/* "Xuất" dropdown — Excel + Đẩy lên Từ điển chung */}
                        <div className="relative" ref={exportMenuRef} data-tour="term-export">
                            <button
                                onClick={() => { setExportMenuOpen(v => !v); setAddMenuOpen(false); }}
                                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 rounded-lg transition-colors"
                            >
                                <Download className="w-4 h-4" />
                                Xuất
                                <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                            </button>
                            {exportMenuOpen && (
                                <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-[100003] py-1 overflow-hidden">
                                    <button
                                        onClick={() => { handleExportCsv(); setExportMenuOpen(false); }}
                                        disabled={groups.length === 0}
                                        className="w-full flex items-start gap-3 px-3 py-2.5 hover:bg-slate-50 text-left disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        <Download className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
                                        <div>
                                            <div className="text-sm font-semibold text-slate-800">Xuất ra Excel</div>
                                            <div className="text-[11px] text-slate-500">Tải file .xlsx — 3 cột: Từ gốc / Bản dịch / Danh mục</div>
                                        </div>
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* "Thêm mới" dropdown — Tạo thủ công + Nhập Excel + Nhập từ điển chung */}
                        <div className="relative" ref={addMenuRef}>
                            <button
                                data-tour="term-add"
                                onClick={() => { setAddMenuOpen(v => !v); setExportMenuOpen(false); }}
                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-lg font-bold text-sm hover:bg-emerald-700 transition-colors shadow-sm shadow-emerald-200"
                            >
                                <Plus className="w-4 h-4" />
                                Thêm mới
                                <ChevronDown className="w-3.5 h-3.5 opacity-80" />
                            </button>
                            {addMenuOpen && (
                                <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-[100003] py-1 overflow-hidden">
                                    <button
                                        onClick={() => { setEditingGroup(undefined); setShowGroupEditor(true); setAddMenuOpen(false); }}
                                        className="w-full flex items-start gap-3 px-3 py-2.5 hover:bg-slate-50 text-left"
                                    >
                                        <Plus className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                                        <div>
                                            <div className="text-sm font-semibold text-slate-800">Tạo thủ công</div>
                                            <div className="text-[11px] text-slate-500">Nhập từng thuật ngữ riêng lẻ</div>
                                        </div>
                                    </button>
                                    <button
                                        onClick={() => { setShowImportModal(true); setAddMenuOpen(false); }}
                                        className="w-full flex items-start gap-3 px-3 py-2.5 hover:bg-slate-50 text-left border-t border-slate-100"
                                    >
                                        <Upload className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
                                        <div>
                                            <div className="text-sm font-semibold text-slate-800">Nhập từ Excel</div>
                                            <div className="text-[11px] text-slate-500">Upload file CSV/TSV/Excel</div>
                                        </div>
                                    </button>
                                    {onPickFromGlobal && (
                                        <button
                                            onClick={() => { onPickFromGlobal(); setAddMenuOpen(false); }}
                                            className="w-full flex items-start gap-3 px-3 py-2.5 hover:bg-slate-50 text-left border-t border-slate-100"
                                        >
                                            <Globe className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
                                            <div>
                                                <div className="text-sm font-semibold text-slate-800">Nhập từ Từ điển chung</div>
                                                <div className="text-[11px] text-slate-500">Chọn thuật ngữ có sẵn từ kho dùng chung</div>
                                            </div>
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Bulk Action Bar — luôn hiện, disable khi chưa chọn */}
                {(() => {
                    const hasSelection = selectedIds.size > 0;
                    const promotable = onAddToGlobal
                        ? groups.filter(g => selectedIds.has(g.id) && !isInGlobal(g)).length
                        : 0;
                    const filteredCats = allCategories.filter((c: string) =>
                        c.toLowerCase().includes(bulkCatInput.toLowerCase().trim())
                    );
                    const canCreateNew = bulkCatInput.trim() &&
                        !allCategories.some(c => c.toLowerCase() === bulkCatInput.toLowerCase().trim());
                    return (
                        <div className={`px-4 py-2.5 border-b flex items-center gap-3 transition-colors ${
                            hasSelection
                                ? 'bg-indigo-50 border-indigo-100'
                                : 'bg-slate-50 border-slate-200'
                        }`}>
                            <div className="flex items-center gap-2 min-w-[140px]">
                                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                                    hasSelection ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-500'
                                }`}>
                                    {selectedIds.size}
                                </span>
                                <span className={`text-sm font-semibold ${hasSelection ? 'text-indigo-700' : 'text-slate-500'}`}>
                                    {hasSelection ? 'đã chọn' : 'chưa chọn'}
                                </span>
                            </div>

                            {/* Gắn danh mục — popover button */}
                            <div className="relative" ref={bulkCatRef}>
                                <button
                                    onClick={() => hasSelection && setBulkCatOpen(o => !o)}
                                    disabled={!hasSelection}
                                    className={`px-3 py-1.5 text-xs font-bold border rounded-lg flex items-center gap-1.5 transition ${
                                        hasSelection
                                            ? 'text-indigo-700 bg-white border-indigo-200 hover:bg-indigo-100'
                                            : 'text-slate-400 bg-white border-slate-200 cursor-not-allowed'
                                    }`}
                                    title={hasSelection ? 'Gắn danh mục cho các thuật ngữ đã chọn' : 'Tick chọn thuật ngữ trước'}
                                >
                                    <Folder className="w-3.5 h-3.5" />
                                    Gắn danh mục
                                    <ChevronDown className="w-3 h-3 opacity-70" />
                                </button>
                                {bulkCatOpen && hasSelection && (
                                    <div className="absolute top-full left-0 mt-1.5 w-80 bg-white border border-slate-200 rounded-xl shadow-2xl z-30 overflow-hidden">
                                        <div className="px-3 py-2 border-b border-slate-100 bg-slate-50">
                                            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                                Gắn danh mục cho {selectedIds.size} thuật ngữ
                                            </div>
                                            <div className="relative">
                                                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                                                <input
                                                    autoFocus
                                                    type="text"
                                                    value={bulkCatInput}
                                                    onChange={(e) => setBulkCatInput(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' && bulkCatInput.trim()) {
                                                            handleBulkAssignCategory(bulkCatInput.trim());
                                                            setBulkCatInput('');
                                                            setBulkCatOpen(false);
                                                        } else if (e.key === 'Escape') {
                                                            setBulkCatOpen(false);
                                                        }
                                                    }}
                                                    placeholder="Tìm hoặc tạo danh mục..."
                                                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-md bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none"
                                                />
                                            </div>
                                        </div>
                                        <div className="max-h-60 overflow-y-auto py-1">
                                            <button
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    handleBulkAssignCategory('');
                                                    setBulkCatInput('');
                                                    setBulkCatOpen(false);
                                                }}
                                                className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 text-slate-600 italic"
                                            >
                                                <X className="w-3.5 h-3.5 text-slate-400" />
                                                Bỏ danh mục (chuyển về "Chưa phân loại")
                                            </button>
                                            {filteredCats.length > 0 && (
                                                <div className="border-t border-slate-100 mt-1 pt-1">
                                                    {filteredCats.map((cat: string) => (
                                                        <button
                                                            key={cat}
                                                            onMouseDown={(e) => {
                                                                e.preventDefault();
                                                                handleBulkAssignCategory(cat);
                                                                setBulkCatInput('');
                                                                setBulkCatOpen(false);
                                                            }}
                                                            className="w-full px-3 py-2 text-left text-sm hover:bg-indigo-50 flex items-center gap-2 text-slate-700 group"
                                                        >
                                                            <Folder className="w-3.5 h-3.5 text-indigo-400 group-hover:text-indigo-600" />
                                                            <span className="font-medium">{cat}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                            {filteredCats.length === 0 && !canCreateNew && (
                                                <div className="px-3 py-4 text-xs text-slate-400 text-center">
                                                    Chưa có danh mục nào.
                                                </div>
                                            )}
                                        </div>
                                        {canCreateNew && (
                                            <button
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    handleBulkAssignCategory(bulkCatInput.trim());
                                                    setBulkCatInput('');
                                                    setBulkCatOpen(false);
                                                }}
                                                className="w-full px-3 py-2.5 text-left text-sm bg-emerald-50 hover:bg-emerald-100 border-t border-emerald-200 flex items-center gap-2 text-emerald-700 font-bold"
                                            >
                                                <Plus className="w-4 h-4" />
                                                <span>Tạo mới: <span className="font-extrabold">"{bulkCatInput.trim()}"</span></span>
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="ml-auto flex items-center gap-2">
                                {onAddToGlobal && (
                                    <button
                                        onClick={handleAddToGlobal}
                                        disabled={!hasSelection || promotable === 0}
                                        title={
                                            !hasSelection ? 'Tick chọn thuật ngữ trước'
                                            : promotable === 0 ? 'Các thuật ngữ đã chọn đều có trong Từ điển chung'
                                            : `Đẩy ${promotable} thuật ngữ chưa có lên Từ điển chung`
                                        }
                                        className="px-3 py-1.5 text-xs font-bold rounded-lg flex items-center gap-1.5 transition text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Globe className="w-3.5 h-3.5" />
                                        Đẩy lên Từ điển chung{hasSelection && promotable > 0 ? ` (${promotable})` : ''}
                                    </button>
                                )}
                                <button
                                    onClick={() => {
                                        if (!confirm(`Xóa ${selectedIds.size} thuật ngữ đã chọn?`)) return;
                                        const newGroups = groups.filter(g => !selectedIds.has(g.id));
                                        setGroups(newGroups);
                                        onUpdateSynonymGroups?.(newGroups);
                                        setSelectedIds(new Set());
                                    }}
                                    disabled={!hasSelection}
                                    className="px-3 py-1.5 text-xs font-bold rounded-lg flex items-center gap-1.5 transition text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    Xóa{hasSelection ? ` (${selectedIds.size})` : ''}
                                </button>
                                <button
                                    onClick={() => setSelectedIds(new Set())}
                                    disabled={!hasSelection}
                                    className="text-xs font-medium text-slate-500 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed px-2"
                                >
                                    Bỏ chọn
                                </button>
                            </div>
                        </div>
                    );
                })()}

                {/* Table */}
                <div className="flex-1 overflow-y-auto p-0 bg-white" data-tour="term-list">
                    <table className="w-full text-left border-collapse">
                        <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 z-10 shadow-sm">
                            <tr>
                                <th className="px-3 py-3 w-10">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.size > 0 && selectedIds.size === filteredGroups.length}
                                        onChange={toggleSelectAll}
                                        className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider w-1/6">Thuật ngữ</th>
                                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider w-1/6">Từ gốc</th>
                                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider w-1/4">Nghĩa đích</th>
                                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider w-32">Ngôn ngữ</th>
                                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider w-36">Danh mục</th>
                                {onAddToGlobal && <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider w-24">Trạng thái</th>}
                                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider w-20 text-right pr-6">Thao tác</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filteredGroups.length === 0 ? (
                                <tr>
                                    <td colSpan={onAddToGlobal ? 8 : 7} className="py-12 text-center text-slate-400">
                                        <FolderPlus className="w-12 h-12 mx-auto mb-3 opacity-20" />
                                        <p className="text-sm font-medium">Chưa có thuật ngữ nào</p>
                                        <p className="text-xs mt-1">Thêm mới thuật ngữ để bắt đầu</p>
                                    </td>
                                </tr>
                            ) : (
                                filteredGroups.map(group => (
                                    <tr key={group.id} className={`hover:bg-slate-50 group transition-colors ${selectedIds.has(group.id) ? 'bg-indigo-50' : ''}`}>
                                        <td className="px-3 py-3 align-top">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(group.id)}
                                                onChange={() => toggleSelect(group.id)}
                                                className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                            />
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <div className="font-bold text-slate-800 text-sm">{group.name}</div>
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <div className="flex flex-wrap gap-1.5">
                                                {group.sources.map((src, i) => (
                                                    <span key={i} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs border border-indigo-100 whitespace-nowrap">
                                                        {src}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <div className="space-y-1">
                                                {group.targets.map((tgt, i) => (
                                                    <div key={i} className="flex items-center gap-2 text-xs">
                                                        <span className={`font-medium ${tgt.isDefault ? 'text-emerald-700' : 'text-slate-600'}`}>
                                                            {tgt.text}
                                                        </span>
                                                        {tgt.context && <span className="text-[10px] italic text-slate-500">({tgt.context})</span>}
                                                        {tgt.isDefault && <span className="px-1 py-0 bg-emerald-100 text-emerald-700 text-[9px] font-bold rounded uppercase">Mặc định</span>}
                                                    </div>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <LangPairBadge
                                                source={group.sourceLang || defaultSourceLang}
                                                target={group.targetLang || defaultTargetLang}
                                            />
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <CategoryDropdown
                                                currentCategory={group.category}
                                                allCategories={allCategories}
                                                onSelect={(cat) => handleUpdateCategory(group.id, cat)}
                                            />
                                        </td>
                                        {onAddToGlobal && (
                                            <td className="px-4 py-3 align-top">
                                                {isInGlobal(group) ? (
                                                    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-bold">
                                                        <Globe className="w-3 h-3" />
                                                        IN GLOBAL
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full font-bold">
                                                        LOCAL
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                        <td className="px-4 py-3 align-top text-right pr-6">
                                            <div className="flex justify-end gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => { setEditingGroup(group); setShowGroupEditor(true); }}
                                                    className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                                    title="Sửa thuật ngữ"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteGroup(group.id)}
                                                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Xoá thuật ngữ"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div >

            {/* Modals */}
            {
                showGroupEditor && (
                    <SynonymGroupEditor
                        group={editingGroup}
                        onSave={handleSaveGroup}
                        onCancel={() => setShowGroupEditor(false)}
                        isOpen={showGroupEditor}
                        existingCategories={Array.from(new Set(groups.map(g => g.category || '').filter(Boolean)))}
                        defaultSourceLang={defaultSourceLang}
                        defaultTargetLang={defaultTargetLang}
                    />
                )
            }

            {
                showImportModal && (
                    <SynonymImportModal
                        onClose={() => setShowImportModal(false)}
                        onImport={handleImportGroups}
                    />
                )
            }
        </div >
    );
};

// Sub-component for Group Card
const GroupCard = ({ group, onEdit, onDelete }: { group: SynonymGroup, onEdit: () => void, onDelete: () => void }) => {
    return (
        <div className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow group relative">
            <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0 pr-4">
                    <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-bold text-slate-800 text-base">{group.name}</h4>
                        {group.category && (
                            <span className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full font-medium border border-slate-200">
                                {group.category}
                            </span>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Sources */}
                        <div>
                            <p className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                                Sources
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                                {group.sources.map((s, idx) => (
                                    <span key={idx} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs font-medium border border-indigo-100">
                                        {s}
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Targets */}
                        <div>
                            <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                                Targets
                            </p>
                            <div className="space-y-1">
                                {group.targets.map((t, idx) => (
                                    <div key={idx} className="flex items-center gap-2 text-sm">
                                        <div className={`w-1.5 h-1.5 rounded-full ${t.isDefault ? 'bg-emerald-500' : 'bg-slate-300'}`}></div>
                                        <span className={t.isDefault ? 'font-bold text-slate-700' : 'text-slate-600'}>{t.text}</span>
                                        {t.context && <span className="text-slate-400 italic text-xs">({t.context})</span>}
                                        {t.isDefault && <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 rounded font-bold ml-auto">MẶC ĐỊNH</span>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity absolute top-4 right-4">
                    <button
                        onClick={(e) => { e.stopPropagation(); onEdit(); }}
                        className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        title="Sửa thuật ngữ"
                    >
                        <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onDelete(); }}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Xoá thuật ngữ"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};


export default TerminologyTab;

